import { useState, useEffect } from "react";
import axios from "../lib/api";
import { format, subDays } from "date-fns";
import { es } from "date-fns/locale";
import {
  GitCompare,
  Calendar,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Minus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const Compare = () => {
  const [loading, setLoading] = useState(true);
  const [periodA, setPeriodA] = useState({
    from: subDays(new Date(), 30),
    to: subDays(new Date(), 1)
  });
  const [periodB, setPeriodB] = useState({
    from: subDays(new Date(), 60),
    to: subDays(new Date(), 31)
  });
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);

  useEffect(() => {
    fetchComparison();
  }, [periodA, periodB]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [responseA, responseB] = await Promise.all([
        axios.get(`${API}/analytics/overview`, {
          params: {
            start_date: format(periodA.from, "yyyy-MM-dd"),
            end_date: format(periodA.to, "yyyy-MM-dd")
          },
          withCredentials: true,
          headers
        }),
        axios.get(`${API}/analytics/overview`, {
          params: {
            start_date: format(periodB.from, "yyyy-MM-dd"),
            end_date: format(periodB.to, "yyyy-MM-dd")
          },
          withCredentials: true,
          headers
        })
      ]);

      setDataA(responseA.data);
      setDataB(responseB.data);
    } catch (error) {
      console.error("Error fetching comparison:", error);
      toast.error("Error al cargar los datos de comparación");
    } finally {
      setLoading(false);
    }
  };

  const calculateChange = (a, b) => {
    if (!b || b === 0) return 0;
    return ((a - b) / b) * 100;
  };

  const ComparisonMetric = ({ label, valueA, valueB, prefix = "", suffix = "", inverse = false }) => {
    const change = calculateChange(valueA, valueB);
    const isPositive = inverse ? change < 0 : change > 0;
    const isNegative = inverse ? change > 0 : change < 0;

    return (
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-6">
          <p className="label-swiss mb-4">{label}</p>
          
          <div className="flex items-center justify-between gap-4">
            <div className="text-center flex-1">
              <p className="text-xs text-slate-500 mb-1">Período A</p>
              <p className="text-2xl font-black font-mono text-slate-900">
                {prefix}{typeof valueA === 'number' ? valueA.toLocaleString('es-ES', { maximumFractionDigits: 2 }) : valueA}{suffix}
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isPositive ? 'bg-green-100' : isNegative ? 'bg-red-100' : 'bg-slate-100'
              }`}>
                {isPositive ? (
                  <TrendingUp className="w-5 h-5 text-green-600" />
                ) : isNegative ? (
                  <TrendingDown className="w-5 h-5 text-red-600" />
                ) : (
                  <Minus className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <span className={`text-sm font-medium mt-1 ${
                isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-slate-500'
              }`}>
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
            </div>
            
            <div className="text-center flex-1">
              <p className="text-xs text-slate-500 mb-1">Período B</p>
              <p className="text-2xl font-black font-mono text-slate-500">
                {prefix}{typeof valueB === 'number' ? valueB.toLocaleString('es-ES', { maximumFractionDigits: 2 }) : valueB}{suffix}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const chartData = dataA && dataB ? [
    { 
      name: 'Gasto', 
      'Período A': dataA.summary.total_spend, 
      'Período B': dataB.summary.total_spend 
    },
    { 
      name: 'Ingresos', 
      'Período A': dataA.summary.total_revenue, 
      'Período B': dataB.summary.total_revenue 
    }
  ] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl text-sm">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: €{entry.value?.toLocaleString('es-ES')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compare-page">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Comparar Períodos</h1>
            <p className="text-slate-600">Analiza el rendimiento entre dos rangos de fechas</p>
          </div>
        </div>

        {/* Date Pickers */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-red-50 border border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="label-swiss text-red-600">Período A (Actual)</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {format(periodA.from, "d MMM", { locale: es })} - {format(periodA.to, "d MMM yyyy", { locale: es })}
                  </p>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-red-200 bg-white" data-testid="period-a-picker">
                      <Calendar className="w-4 h-4 mr-2" />
                      Cambiar
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="range"
                      selected={periodA}
                      onSelect={(range) => range && setPeriodA(range)}
                      numberOfMonths={2}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-100 border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="label-swiss text-slate-600">Período B (Comparación)</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {format(periodB.from, "d MMM", { locale: es })} - {format(periodB.to, "d MMM yyyy", { locale: es })}
                  </p>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-slate-200 bg-white" data-testid="period-b-picker">
                      <Calendar className="w-4 h-4 mr-2" />
                      Cambiar
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="range"
                      selected={periodB}
                      onSelect={(range) => range && setPeriodB(range)}
                      numberOfMonths={2}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : dataA && dataB ? (
          <>
            {/* KPI Comparison */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ComparisonMetric
                label="Gasto Total"
                valueA={dataA.summary.total_spend}
                valueB={dataB.summary.total_spend}
                prefix="€"
                inverse={true}
              />
              <ComparisonMetric
                label="Ingresos"
                valueA={dataA.summary.total_revenue}
                valueB={dataB.summary.total_revenue}
                prefix="€"
              />
              <ComparisonMetric
                label="ROAS"
                valueA={dataA.summary.roas}
                valueB={dataB.summary.roas}
                suffix="x"
              />
              <ComparisonMetric
                label="Conversiones"
                valueA={dataA.summary.total_conversions}
                valueB={dataB.summary.total_conversions}
              />
              <ComparisonMetric
                label="CPA"
                valueA={dataA.summary.cpa}
                valueB={dataB.summary.cpa}
                prefix="€"
                inverse={true}
              />
              <ComparisonMetric
                label="% Gasto/Ingresos"
                valueA={dataA.summary.spend_percentage}
                valueB={dataB.summary.spend_percentage}
                suffix="%"
                inverse={true}
              />
            </div>

            {/* Comparison Chart */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">Gasto vs Ingresos</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Período A" fill="#DC2626" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Período B" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Platform Comparison */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">Comparación por Plataforma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Plataforma</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Gasto A</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Gasto B</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">ROAS A</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">ROAS B</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Cambio ROAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataA.platforms.map((platformA, index) => {
                        const platformB = dataB.platforms[index];
                        const roasChange = calculateChange(platformA.roas, platformB?.roas);
                        return (
                          <tr key={platformA.platform} className="border-b border-slate-100">
                            <td className="py-3 px-4 font-medium text-slate-900">
                              {platformA.platform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </td>
                            <td className="py-3 px-4 text-right font-mono">€{platformA.spend.toLocaleString('es-ES')}</td>
                            <td className="py-3 px-4 text-right font-mono text-slate-500">€{platformB?.spend.toLocaleString('es-ES')}</td>
                            <td className="py-3 px-4 text-right font-mono">{platformA.roas}x</td>
                            <td className="py-3 px-4 text-right font-mono text-slate-500">{platformB?.roas}x</td>
                            <td className={`py-3 px-4 text-right font-mono ${roasChange > 0 ? 'text-green-600' : roasChange < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                              {roasChange > 0 ? '+' : ''}{roasChange.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-600">No hay datos disponibles para comparar</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Compare;
